import { ICreateInternalNote, IUpdateInternalNote, IUser } from '@sdk/contracts';
import { Button, Form } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyInternalNotes } from './EmptyInternalNotes';
import {
  FormWrapper,
  ColumnWrapper,
  FiltersWrapper,
  ItemsWrapper,
  Wrapper,
  FormItemsWrapper,
  FormHeaderWrapper,
  FormFooterWrapper
} from './StudentInternalNotes.styles';

import {
  InternalNoteCard,
  InternalNoteForm,
  InternalNotesFilters,
  FormHeader
} from '@/components/InternalNotes';
import { useInternalNotesForm } from '@/hooks';
import { useMutateInternalNotes, useProfile, useStudentInternalNotesList } from '@/store';

type Props = {
  student: IUser;
};

export const StudentInternalNotes: React.FC<Props> = ({ student }) => {
  const { t } = useTranslation('ChatTranslations');
  const { internalNotes, filtersCount, search } = useStudentInternalNotesList(student._id);
  const { openDrawer, internalNote, closeDrawer, resetNote } = useInternalNotesForm();
  const { profile } = useProfile();
  const { createInternalNote, isWorking, deleteInternalNote, updateInternalNote } =
    useMutateInternalNotes();
  const [form] = Form.useForm();
  const title = Form.useWatch('title', form);
  const highlightedBy = Form.useWatch('highlightedBy', form);

  useEffect(() => {
    form.resetFields();
    resetNote();
    if (internalNotes && internalNotes.length > 0) {
      openDrawer(internalNotes[0]);
    }
  }, [internalNotes, student._id]);

  useEffect(() => {
    form.resetFields();
    if (internalNote) {
      form.setFieldsValue(internalNote);
    }
  }, [internalNote, student._id]);

  const onFinish = (values: ICreateInternalNote | IUpdateInternalNote) => {
    if (!student) return;
    if (internalNote) {
      updateInternalNote({
        ...values,
        _id: internalNote._id,
        isForManagers: values.isForManagers ?? false
      });
    } else {
      createInternalNote({
        ...values,
        student: student._id,
        isForManagers: values.isForManagers ?? false
      });
    }
  };

  const handleDelete = () => {
    if (!internalNote) return;
    deleteInternalNote(internalNote._id);
  };

  const handleCreateClick = () => {
    closeDrawer();
    resetNote();
    form.resetFields();
  };

  const handleToggleHighlight = () => {
    form.setFieldsValue({ highlightedBy: highlightedBy ? null : profile?._id });
  };

  return (
    <Wrapper>
      <ColumnWrapper>
        {internalNotes?.length === 0 && !filtersCount && !search ? (
          <EmptyInternalNotes studentName={student.name} />
        ) : (
          <>
            <FiltersWrapper>
              <InternalNotesFilters
                onCreateClick={handleCreateClick}
                studentId={student._id}
                studentName={student.name}
              />
            </FiltersWrapper>
            <ItemsWrapper>
              {internalNotes?.map(note => (
                <InternalNoteCard
                  key={note._id}
                  note={note}
                  isSelected={note._id === internalNote?._id}
                />
              ))}
            </ItemsWrapper>
          </>
        )}
      </ColumnWrapper>
      <FormItemsWrapper>
        <div style={{ width: '100%' }}>
          <FormHeaderWrapper>
            <FormHeader
              isHighlighted={!!highlightedBy}
              onToggleHighlight={handleToggleHighlight}
              isEditing={!!internalNote}
              isWorking={isWorking}
              onDelete={handleDelete}
            />
          </FormHeaderWrapper>
          <FormWrapper>
            <InternalNoteForm form={form} onFinish={onFinish} />
          </FormWrapper>
        </div>
        <FormFooterWrapper>
          <Button type="primary" onClick={form.submit} disabled={!title}>
            {t('SAVE_NOTE')}
          </Button>
        </FormFooterWrapper>
      </FormItemsWrapper>
    </Wrapper>
  );
};
